/* eslint-disable no-param-reassign */
/* eslint-disable default-case */
/* eslint-disable @typescript-eslint/no-unsafe-call */
// @ts-nocheck
// import * as HXKlineChart from '@ths-m/HXKlineChart';
// import * as HXKline from '@ths-m/HXKline';
import { type Layer, type HXKLineLayerSpec, defineHXKLineLayer } from '../../../core/layer';
import type { StockData, KLineData, ExtendBarSeries } from '../../../helper/hxKLine';
import type { KLineTooltipItem } from '../ui/kLineTooltip/KLineTooltip';
import { validateHXKlineComponents } from '../../../util/hxKLine';

validateHXKlineComponents();
HXKline.setHXKlineChart(HXKlineChart);

const visInfo: Layer['visInfo'] = {
  id: 'hxKLineAndBar',
  name_en: 'Candlestick Chart And Bar',
  // name_zh: '行情图叠加柱状图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  parameters: {},
  extra: {
    isNotTrainedByLLM: true
  }
};

export interface Spec extends HXKLineLayerSpec {
  /** 柱状图使用的数据集索引，默认为 0 */
  dataIndex?: number;
  /** 柱状图数据编码 */
  encoding: {
    x: string;
    y: string;
  };
  stocks: StockData[];
}

/** 暂不支持legend的点击功能 */
export const layer = defineHXKLineLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  // eslint-disable-next-line max-lines-per-function, sonarjs/cognitive-complexity
  render(api, spec, $dom) {
    const { layout, data: dataArray, hook, util, ui, token } = api;
    let legendIns: InstanceType<(typeof ui)['Legend']['Legend']>;
    let kLineTooltipIns: InstanceType<(typeof ui)['KLineTooltip']>;
    let tooltipIns: InstanceType<(typeof ui)['Tooltip']>;
    let hxKLine: { destroy: () => void };
    let dataZoomIns: InstanceType<(typeof ui)['CustomDataZoom']>;

    HXKline.verifyProjectPermission(hook.globalConfig(api).hxKLineVerifyInfo);

    // * -------------------------- dom
    const $toolbarLegend = document.createElement('section');
    const $tooltipContainer = document.createElement('section');
    const $chart = document.createElement('section');
    const $leftYAxis = document.createElement('section');
    const $kline = document.createElement('section');
    const $dataZoomContainer = document.createElement('section');
    const lineColor = token?.dvHXKLine?.constants?.DEFAULT_TICK_LINE_COLOR;
    const borderLineStyle = `1px solid ${lineColor || 'rgba(125, 133, 146, 0.3)'}`;
    const axisTickColor = token?.dvHXKLine?.constants?.DEFAULT_FONT_COLOR;

    $dom.style.cssText +=
      'display: flex; flex-direction: column; justify-content: center; align-items: center;';

    $toolbarLegend.style.cssText = 'width: 100%; margin-bottom: 8px;';
    $chart.style.cssText =
      'position: relative; flex: 1; width: 100%; height: 0; display: flex; flex-direction: row;';
    $leftYAxis.style.cssText = `
      cursor: default;
      font-family: Microsoft YaHei;
      width: fit-content;
      min-width: 20px;
      height: 100%;
      font-size: 12px;
      color: ${axisTickColor};
      overflow: hidden;
      text-align: right;
      padding-right: 6px;
      border-right: ${borderLineStyle};
    `;
    $kline.style.cssText = `
      position: relative;
      flex: 1;
      width: 0;
      height; 100%;
    `;
    $kline.setAttribute('id', util.common.generateUID());

    $chart.appendChild($leftYAxis);
    $chart.appendChild($kline);
    $dom.appendChild($toolbarLegend);
    $dom.appendChild($tooltipContainer);
    $dom.appendChild($chart);
    $dom.appendChild($dataZoomContainer);

    // * -------------------------- data
    const {
      dataIndex = 0,
      encoding: { x, y },
      stocks
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);
    const transformData = util.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: x, order: 'ascending' },
      { type: 'deDuplication', field: x }
    ]) as (typeof data)['values'];
    const { x: xAxisData, y: yAxisData } = util.dataEncoding.encoding(transformData, {
      x: { field: x },
      y: { field: y, transforms: [{ type: 'toNumeric' }] }
    });

    const YAxisSplitNum = 5;
    const barColor = 'rgba(85, 92, 247, 0.16)';
    const highlightBarColor = 'rgba(85, 92, 247, 0.35)';
    const drawBarObject = {
      color: barColor,
      unit: dataMetaInfo[y]?.unit || '',
      highlightColor: highlightBarColor,
      gap: 0.1
    };
    const hxKLineAndBarToken = util.data.deepMerge(
      {
        option: {
          yAxis: {
            splitNum: YAxisSplitNum
          }
        }
      },
      token?.dvHXKLine?.hxKLineAndBar ?? {},
      { override: true }
    );

    // 自定义指标——数据,生成柱子数据
    const barList = [] as {
      timestamp: number | string;
      value: number;
    }[];

    xAxisData.forEach((date, index) => {
      barList.push({
        timestamp: new Date(date).getTime(),
        value: yAxisData[index]
      });
    });

    // * -------------------------- legend
    const selectedStockIndex = 0;
    let selectedStock = stocks[selectedStockIndex];

    const legendData = [y].map(item => ({
      name: item,
      color: barColor,
      type: util.const.SERIES_2_LEGEND_TYPE_MAP.bar,
      selected: true,
      disableSelect: true
    }));
    legendData.push({
      type: ui.Legend.ShowType.KLine,
      stocks,
      defaultValueIndex: selectedStockIndex
    });

    legendIns = new ui.Legend.Legend($toolbarLegend, {
      data: legendData,
      layout: 'pagination',
      token
    });

    // * -------------------------- dataZoom
    dataZoomIns = new ui.CustomDataZoom($dataZoomContainer, {
      data: xAxisData,
      token
    });

    // * -------------------------- tooltip
    /** 柱子的tooltip */
    tooltipIns = new ui.Tooltip({
      container: $chart,
      formatter: dataIndex_ => {
        return hook.buildTooltip({
          config: hook.buildStandardChartPresetOption(api).tooltip,
          title: xAxisData[dataIndex_],
          items: [y].map(item => {
            const result = hook.formatDataNumberValue(barList[dataIndex_].value, {
              type: 'table',
              unit: dataMetaInfo[y]?.unit
            });

            return {
              type: util.const.LEGEND_TYPE.Rect,
              color: barColor,
              name: item,
              value: result.value + result.unit
            };
          }),
          noWrapper: true
        });
      }
    });

    /** 行情图的tooltip */
    kLineTooltipIns = new ui.KLineTooltip($tooltipContainer, {
      token
    });
    function showKLineTooltip(kLineIns: unknown, dataIndex_?: number | undefined) {
      const {
        defaultTooltipData: defaultKLineTooltip
      }: { defaultTooltipData: KLineTooltipItem[] } = hook.getHXKLineTooltipData(
        kLineIns,
        'kline',
        dataIndex_
      );

      const { LAYOUT } = util.const;
      let showTooltip = defaultKLineTooltip;
      if (layout === LAYOUT.SMALL || layout === LAYOUT.SMALL_1_2) {
        // 设计师规定，卡片宽度较小时只显示三项tooltip字段
        showTooltip = showTooltip.slice(0, 3);
      }
      kLineTooltipIns.show(showTooltip);
    }

    // * -------------------------- chart
    let [dataZoomStartIndex, dataZoomEndIndex] = [0, xAxisData.length - 1];

    // 初始化左侧Y轴;
    const initLeftYAxis = ({ min, max }) => {
      const range = max - min;
      const { height, width } = hxKLine.getSize('candle_pane', 'yAxis');
      $leftYAxis.style.height = `${height}px`;
      $leftYAxis.style.width = 'fit-content';

      for (let i = 0; i < YAxisSplitNum + 1; i++) {
        const yAxisValue = ((range / YAxisSplitNum) * i + min) as number;
        const result = hook.formatDataNumberValue(yAxisValue);
        const tempHeight = 100 / YAxisSplitNum + (i % (YAxisSplitNum - 1) !== 0 ? '%' : '% - 8px');
        $leftYAxis.innerHTML += `<div style="height: calc(${tempHeight})">${
          result.value + result.unit
        } ${drawBarObject.unit}</div>`;
      }
    };

    // 更新左侧Y轴
    const updateLeftYAxis = ({ min, max }) => {
      const gapValue = (max - min) * drawBarObject.gap;
      const realMax = (max + gapValue) as number;
      const range = realMax - min;
      const leftYAxisLabels = $leftYAxis.childNodes;

      for (let i = 0; i < YAxisSplitNum + 1; i++) {
        const yAxisValue = ((range / YAxisSplitNum) * i + min) as number;
        leftYAxisLabels[YAxisSplitNum - i].innerHTML = `${yAxisValue.toFixed(2)} ${
          drawBarObject.unit
        }`;
      }
    };

    function renderHXKLine() {
      if (hxKLine) {
        HXKline.disposeChart(
          (hxKLine._chartContainer as HTMLElement).parentElement?.getAttribute('id'),
          (...args) => {
            console.log('hxKLine destroy', ...args);
          }
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      hxKLine = HXKline.initChart(
        $kline.getAttribute('id'),
        /**
         * @see https://khtest.10jqka.com.cn/kodexplorer/data/User/wangyifan/home/HXKlineChart/docs/guide/datafeed-datasource.html
         */
        {
          id: util.common.generateUID(),
          code: selectedStock.code,
          market: selectedStock.market,
          data_class: 'kline',
          time_period: 'day_1',
          trade_date: [
            new Date(xAxisData[dataZoomStartIndex]),
            new Date(xAxisData[dataZoomEndIndex])
          ],
          // trade_date: 0,
          adjust_type: 'forward'
        },
        /**
         * @see https://khtest.10jqka.com.cn/kodexplorer/data/User/wangyifan/home/HXKlineChart/docs/guide/chart-api.html
         */
        {
          chartType: '',
          dateFormat: 'YYYY-MM-DD',
          locale: 'zh_CN',
          styles: 'customTheme'
        },
        (...args) => {
          console.log(...args);

          if (args[0] === 'success') {
            hxKLine.setZoomEnabled(false);
            hxKLine.setScrollEnabled(false);
            hxKLine.setBarSpaceLimit({ min: 0, max: 50 });
            hxKLine.zoomToShowAllData();
            hxKLine.setStyles(hxKLineAndBarToken.option);

            initLeftYAxis(hxKLine.getYAxisRangeInfo());

            // 根据 k 线数据集调整给定数据集的时间戳
            const dataList = util.hxKLine
              .adaptData(
                hxKLine.getDataList() as KLineData,
                barList.slice(dataZoomStartIndex, dataZoomEndIndex + 1).map((item, index) => ({
                  timestamp: new Date(xAxisData[dataZoomStartIndex + index]).getTime(),
                  dataIndex: dataZoomStartIndex + index,
                  value: item.value
                }))
              )
              .map(item => ({
                ...item,
                value: item.__originData.value,
                dataIndex: item.__originData.dataIndex
              }));

            drawBarObject.barData = dataList;

            hxKLine.createOverlay({
              name: 'bar',
              points: [dataList[0]],
              // 传入起始坐标点
              extendData: {
                ...drawBarObject,
                hxKLine,
                onUpdateLeftYAxis: updateLeftYAxis,
                onMouseEnter: event => {
                  if (event.dataIndex === '') {
                    return;
                  }

                  tooltipIns.show([event.x, event.y], event.dataIndex);
                },
                onMouseLeave: () => {
                  tooltipIns.hide();
                }
              }
            } as ExtendBarSeries);

            // 处理 dom 相关的问题
            const $main = $kline.querySelector('div > div > div:nth-child(1)') as HTMLElement;
            const $xAxis = $kline.querySelector(
              'div > div:nth-child(2) > div:nth-child(1)'
            ) as HTMLElement;
            const $yAxis = $kline.querySelector('div > div > div:nth-child(2)') as HTMLElement;
            $xAxis.style.cursor = 'default';
            $yAxis.style.cursor = 'default';
            if ($main) {
              $main.style.borderTop = borderLineStyle;
            }
            const $leftYAxisName = document.createElement('div');
            const $rightYAxisName = document.createElement('div');
            const $xAxisName = document.createElement('div');
            $leftYAxisName.style.cssText =
              'position: absolute; left:4.5px; top: 4px;font-size: 12px; color: rgba(0, 0, 0, 0.45); z-index: 2';
            $rightYAxisName.style.cssText =
              'position: absolute; right:4.5px; top: 4px;font-size: 12px; color: rgba(0, 0, 0, 0.45); z-index: 2';
            $xAxisName.style.cssText =
              'position: absolute; right:4.5px; bottom: 4px;font-size: 12px; color: rgba(0, 0, 0, 0.45); z-index: 2;';
            $rightYAxisName.innerHTML = '股价(元)';
            $leftYAxisName.innerHTML =
              y + (dataMetaInfo[y]?.unit ? `(${dataMetaInfo[y].unit})` : '');
            $xAxisName.innerHTML = x;
            $main.appendChild($leftYAxisName);
            $main.appendChild($rightYAxisName);
            $main.appendChild($xAxisName);

            // 初始tooltip展示最近日期的数据
            showKLineTooltip(hxKLine);

            /**
             * - gap 限制主图内容的上下间隔
             * - scrollZoomEnabled 限制y轴的拖动
             */
            hxKLine.setPaneOptions({
              id: 'candle_pane',
              gap: {
                top: 0.05,
                bottom: 0.05
              },
              axisOptions: { scrollZoomEnabled: false }
            });

            /** 实现自定义 tooltip */
            hxKLine.subscribeAction('onCrosshairChange', (...rests) => {
              if (!rests[0].kLineData) {
                return;
              }

              showKLineTooltip(hxKLine, rests[0].dataIndex);
            });
          }
        }
      );
    }

    dataZoomIns.chartIns.on('datazoom', event => {
      const startIndex = Math.round((event.start / 100) * (xAxisData.length - 1));
      const endIndex = Math.round((event.end / 100) * (xAxisData.length - 1));

      if (startIndex !== dataZoomStartIndex || endIndex !== dataZoomEndIndex) {
        // 设置行情图的区间
        [dataZoomStartIndex, dataZoomEndIndex] = [startIndex, endIndex];

        renderHXKLine();
      }
    });

    legendIns.klineDropdown[0].on('change', event => {
      selectedStock = stocks.find(item => item.code === event.key);

      renderHXKLine();
    });

    renderHXKLine();

    return () => {
      legendIns?.destroy?.();
      kLineTooltipIns?.destroy?.();
      tooltipIns?.destroy?.();
      dataZoomIns?.destroy?.();
      HXKline.disposeChart($kline, (...args) => {
        console.log('hxKLine destroy', ...args);
      });

      $dom.innerHTML = '';
    };
  }
});
